'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { initiateAuthForOtp, login, resendOtp } from '@/app/_authAction';
import BasicButton from '@/components/basics/BasicButton';
import BasicInput from '@/components/basics/BasicInput';
import ErrorMessages from '@/components/basics/ErrorMessages';
import FormOTPInput from '@/components/features/form/FormOTPInput';
import FormPassword from '@/components/features/form/FormPassword';
import { formatRemainingTime } from '@/lib/auth/otp-time';

const OTP_EXPIRY_SECONDS = 180; // 3 分（Cognito auth_session_validity と合わせる）
const OTP_RESEND_COOLDOWN_SECONDS = 60;
// Cognito は EMAIL_OTP チャレンジで連続 3 回失敗すると Session を無効化する。
// フロント側でも同じ閾値でカウントし、上限到達時に専用文言を出す。
const OTP_MAX_ATTEMPTS = 3;

type Step = 'credentials' | 'otp';

export default function SignInPage() {
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [cognitoSession, setCognitoSession] = useState('');
  const [otpDestination, setOtpDestination] = useState('');
  const [expirySecondsLeft, setExpirySecondsLeft] = useState(OTP_EXPIRY_SECONDS);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(OTP_RESEND_COOLDOWN_SECONDS);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const isOtpAttemptsExceeded = otpAttempts >= OTP_MAX_ATTEMPTS;

  const searchParams = useSearchParams();
  const callbackUrl = searchParams?.get('callbackUrl') || '/home';

  // OTP ステップ滞在中のカウントダウン
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (step !== 'otp') {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setExpirySecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
      setResendSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [step]);

  const resetToCredentials = () => {
    setStep('credentials');
    setOtpCode('');
    setCognitoSession('');
    setOtpDestination('');
    setExpirySecondsLeft(OTP_EXPIRY_SECONDS);
    setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
    setOtpAttempts(0);
    setErrorMessages([]);
  };

  const handleCredentialsSubmit = async () => {
    try {
      setIsLoading(true);
      setErrorMessages([]);
      const result = await initiateAuthForOtp({ email, password });

      switch (result.kind) {
        case 'email_otp_required':
          setCognitoSession(result.session);
          setOtpDestination(result.destination);
          setExpirySecondsLeft(OTP_EXPIRY_SECONDS);
          setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
          setStep('otp');
          break;
        case 'password_only':
          await login({ email, password, redirectTo: callbackUrl, redirect: true }, 'credentials');
          break;
        case 'new_password_required':
          window.location.href = `/auth/signup?email=${encodeURIComponent(email)}&code=${encodeURIComponent(password)}`;
          break;
        case 'error':
          setErrorMessages(['メールアドレスまたはパスワードが間違っています。']);
          break;
      }
    } catch (err) {
      console.error(err);
      setErrorMessages(['メールアドレスまたはパスワードが間違っています。']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (codeOverride?: string) => {
    // FormOTPInput.onComplete から呼ばれる時、親 state (otpCode) はまだ更新前なので
    // 引数で渡される完成済みコードを優先する。手動でボタン押下時は state を見る。
    const code = codeOverride ?? otpCode;
    if (code.length !== 6) return;
    if (isOtpAttemptsExceeded) return;
    if (isLoading) return; // 二重 submit 防止 (auto-complete 連発・連打対策)
    try {
      setIsLoading(true);
      setErrorMessages([]);
      await login(
        { email, password, otpCode: code, cognitoSession, redirectTo: callbackUrl, redirect: true },
        'credentials',
      );
    } catch (err) {
      // login (signIn) は次の経路でエラーを throw する:
      //  - AuthError (CredentialsSignin): Cognito 認証失敗（OTP 不一致 / 期限切れ / Session 失効）
      //  - その他 (NEXT_REDIRECT): redirect: true 時のリダイレクト → これは正常系
      //  - 真のネットワーク例外: 通信エラー
      // NEXT_REDIRECT は throw に見えるが正常終了の合図なので、エラーメッセージや
      // attempts には含めない（next-auth の標準パターン）。
      const isRedirect = err instanceof Error && /NEXT_REDIRECT/i.test(err.message ?? err.toString());
      if (isRedirect) {
        throw err;
      }
      console.error('OTP submit error:', err);
      // OTP 認証失敗扱いとして attempts を増やすが、AuthError 以外（純粋なネットワーク例外）は
      // ベストエフォートで判別困難なため、原則「OTP エラー」として扱う。
      // ただし expirySecondsLeft <= 0 の場合は期限切れ表示を優先する。
      const nextAttempts = otpAttempts + 1;
      setOtpAttempts(nextAttempts);
      if (expirySecondsLeft <= 0) {
        setErrorMessages(['認証コードの有効期限が切れました。再送してください。']);
        return;
      }
      if (nextAttempts >= OTP_MAX_ATTEMPTS) {
        // Cognito 仕様で連続 3 回失敗時に Session が無効化される。
        // フロント側でも同じ閾値で専用文言に切り替え、再送/最初からやり直しに誘導する。
        setErrorMessages(['試行回数を超えました。最初からやり直してください。']);
        return;
      }
      setErrorMessages(['認証コードが正しくありません。もう一度お試しください。']);
      setOtpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      setErrorMessages([]);
      const result = await resendOtp({ email, password });
      if (result.success) {
        setCognitoSession(result.session);
        setOtpDestination(result.destination);
        setOtpCode('');
        setExpirySecondsLeft(OTP_EXPIRY_SECONDS);
        setResendSecondsLeft(OTP_RESEND_COOLDOWN_SECONDS);
        setOtpAttempts(0);
      } else {
        setErrorMessages(['認証コードの再送に失敗しました。最初からやり直してください。']);
      }
    } catch (err) {
      console.error(err);
      setErrorMessages(['認証コードの再送に失敗しました。']);
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (step === 'credentials') {
        handleCredentialsSubmit();
      } else if (step === 'otp' && otpCode.length === 6) {
        handleOtpSubmit();
      }
    }
  };

  if (step === 'otp') {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="flex w-[360px] flex-col items-center gap-6">
          <Image priority src="/images/header_logo.svg" alt="Kuden Business Logo" width={160} height={20} />
          <h1 className="text-2xl font-bold">認証コードを入力</h1>
          <p className="text-sm text-default-600">
            {otpDestination ? `${otpDestination} 宛に` : '登録メールアドレス宛に'}
            6桁の認証コードを送信しました。
          </p>
          <p
            className={`text-sm ${expirySecondsLeft <= 0 ? 'text-danger' : 'text-default-600'}`}
            aria-live="polite"
            data-testid="otp-remaining-time"
          >
            残り時間: {formatRemainingTime(expirySecondsLeft)}
          </p>
          <div className="flex w-full flex-col gap-2">
            <label className="font-semibold">認証コード</label>
            <FormOTPInput
              value={otpCode}
              onChange={setOtpCode}
              onComplete={code => handleOtpSubmit(code)}
              disabled={isLoading || isResending || expirySecondsLeft <= 0 || isOtpAttemptsExceeded}
              autoFocus
              error={errorMessages.length > 0}
              placeholder="123456"
            />
          </div>
          {errorMessages.length > 0 && <ErrorMessages errors={errorMessages} />}
          <BasicButton
            className="w-full"
            color="primary"
            isLoading={isLoading}
            isDisabled={otpCode.length !== 6 || expirySecondsLeft <= 0 || isResending || isOtpAttemptsExceeded}
            onPress={() => handleOtpSubmit()}
            id="otp-verify-button"
            aria-label="認証コードを確認"
          >
            確認
          </BasicButton>
          <div className="flex w-full flex-col items-center gap-3">
            <BasicButton
              variant="light"
              color="primary"
              className="data-[hover=true]:bg-transparent"
              isLoading={isResending}
              isDisabled={resendSecondsLeft > 0 || isLoading}
              onPress={handleResend}
              id="otp-resend-button"
              aria-label="認証コードを再送"
            >
              {resendSecondsLeft > 0 ? `再送（${resendSecondsLeft}s）` : '認証コードを再送'}
            </BasicButton>
            <button
              className="cursor-pointer text-sm text-default-500 disabled:cursor-not-allowed"
              type="button"
              onClick={resetToCredentials}
              disabled={isLoading || isResending}
            >
              サインインに戻る
            </button>
          </div>
          <p className="text-center text-xs text-default-500">
            メールが届かない場合は迷惑メールフォルダをご確認ください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <Image priority src="/images/header_logo.svg" alt="Kuden Business Logo" width={160} height={20} />
        <h1 className="text-2xl font-bold">ログイン</h1>
        <div className="flex flex-col gap-2">
          <label className="font-semibold">メールアドレス</label>
          <BasicInput
            className="w-[300px]"
            type="email"
            placeholder="example@kuden.co.jp"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-semibold">パスワード</label>
          <FormPassword
            className="w-[300px]"
            placeholder="パスワードを入力してください"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        {errorMessages.length > 0 && <ErrorMessages errors={errorMessages} />}
        <BasicButton
          className="w-[200px]"
          color="primary"
          isLoading={isLoading}
          onPress={() => handleCredentialsSubmit()}
          id="login-button"
          name="login"
          type="submit"
          aria-label="ログイン"
        >
          ログイン
        </BasicButton>
        <Link className="cursor-pointer text-primary" href="/auth/password/reset">
          パスワードを忘れた場合
        </Link>
      </div>
    </div>
  );
}
